import { MILLISECONDS_PER_MINUTE, MINUTES_PER_HOUR } from '@web/dtos';

export function toInputDate(displayDate: string): string {
  if (!displayDate || !displayDate.includes('.')) {
    return '';
  }

  const [day, month, year] = displayDate.split('.');
  return `${year}-${month}-${day}`;
}

export function fromInputDate(inputDate: string): string {
  if (!inputDate) {
    return '';
  }

  const [year, month, day] = inputDate.split('-');
  return `${day}.${month}.${year}`;
}

export function parseIsoDate(
  value: string | null | undefined
): Date | undefined {
  if (!value) {
    return undefined;
  }

  const [year, month, day] = value.split('T')[0].split('-').map(Number);
  if (!year || !month || !day) {
    return undefined;
  }

  return new Date(year, month - 1, day);
}

export function formatDateOnly(value: Date): string;
export function formatDateOnly(value: undefined): null;
export function formatDateOnly(value: Date | undefined): string | null;
export function formatDateOnly(value: Date | undefined): string | null {
  if (!value) {
    return null;
  }

  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, '0'),
    String(value.getDate()).padStart(2, '0'),
  ].join('-');
}

/** Formats an ISO timestamp in the local timezone of the current runtime. */
export function formatIsoTime(
  value: string | number | Date | null | undefined
): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}`;
}

export function formatSimpleTime(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const [hours, minutes] = value.split(':');
  return `${hours}:${minutes}`;
}

export function calculateTimeDeviationMinutes(
  actualTimestamp: string,
  plannedDate: string,
  plannedTime: string
): number | null {
  const actual = Date.parse(actualTimestamp);
  const planned = Date.parse(`${plannedDate}T${plannedTime}`);

  if (!Number.isFinite(actual) || !Number.isFinite(planned)) {
    return null;
  }

  const deviation = Math.round((actual - planned) / MILLISECONDS_PER_MINUTE);
  if (deviation === 0) {
    return null;
  }

  return deviation;
}

export function formatFullIsoDateTimeOffset(value: Date): string | null {
  if (!value || !Number.isFinite(value.getTime())) {
    return null;
  }

  const date = formatDateOnly(value);
  const time = `${formatIsoTime(value)}:${String(value.getSeconds()).padStart(2, '0')}.${String(value.getMilliseconds()).padStart(3, '0')}`;
  const zoneOffset = formatTimezoneOffset(-value.getTimezoneOffset());

  return `${date}T${time}${zoneOffset}`;
}

export function formatTimezoneOffset(timezoneOffsetMinutes: number): string {
  const { sign, hours, minutes } = getOffsetHoursAndMinutes(
    timezoneOffsetMinutes
  );

  const signString = sign === -1 ? '-' : '+';
  const hourString = String(hours).padStart(2, '0');
  const minuteString = String(minutes).padStart(2, '0');

  return `${signString}${hourString}:${minuteString}`;
}

export function getOffsetHoursAndMinutes(timezoneOffsetMinutes: number): {
  sign: -1 | 1;
  hours: number;
  minutes: number;
} {
  const sign = timezoneOffsetMinutes < 0 ? -1 : 1;
  let offsetMinutesTotal = Math.abs(timezoneOffsetMinutes);
  let offsetHours = 0;

  while (offsetMinutesTotal >= MINUTES_PER_HOUR) {
    offsetMinutesTotal -= MINUTES_PER_HOUR;
    offsetHours++;
  }

  return {
    sign,
    hours: offsetHours,
    minutes: offsetMinutesTotal,
  };
}
